import { sumBy } from 'lodash'

import { USDC_TOKENS } from '~/constants/addresses'
import { OrgSmartAccount, SmartAccountType } from '~/types'
import { TokenBalance } from '~/utils/account'
import { Address } from '~/utils/address'
import { getAutoEarnModule, supportsAutoEarn } from '~/utils/vault'

type Account = Pick<
  OrgSmartAccount,
  'address' | 'isArchived' | 'role' | 'smartAccountModules' | 'type'
>
type Balance = Pick<TokenBalance, 'address' | 'chainId' | 'usdValue'>

// Workspace auto-earn only enrolls the Treasury and its operating accounts, so
// USDC anywhere else would stay idle after turning it on. Same accounts as the
// idle USDC email on the server.
const isEnrolledByWorkspace = (account: Account) =>
  !account.isArchived &&
  account.type === SmartAccountType.SPLITS_VAULT_V1 &&
  supportsAutoEarn(account)

// `eligibleChainIds` are the networks turning auto-earn on would cover, as the
// server reports them.
export const getIdleUsdcValue = ({
  accounts,
  balances,
  eligibleChainIds,
  thresholdUsd,
}: {
  accounts: Account[]
  balances: Record<string, { balances: Balance[] }>
  eligibleChainIds: number[]
  thresholdUsd: number
}): number | undefined => {
  const idleUsdcValue = sumBy(
    accounts.filter(isEnrolledByWorkspace).flatMap((account) => {
      // An account with its own auto-earn 2.0 on a chain deposits USDC there
      // without the workspace toggle, so it isn't idle. A paused module still
      // counts as on. The server no longer processes 1.0, so it's ignored.
      const chainIds = eligibleChainIds.filter(
        (chainId) => !getAutoEarnModule(account, chainId),
      )
      return (balances[account.address]?.balances ?? []).filter((balance) =>
        chainIds.some(
          (chainId) =>
            balance.chainId === chainId &&
            Address.equal(balance.address, USDC_TOKENS[chainId]),
        ),
      )
    }),
    (balance) => balance.usdValue,
  )

  return idleUsdcValue > thresholdUsd ? idleUsdcValue : undefined
}
